import {
  CalendarEventExternal,
  createViewDay,
  createViewList,
  createViewMonthAgenda,
  createViewMonthGrid,
  createViewWeek,
} from "@schedule-x/calendar";
import { createCurrentTimePlugin } from "@schedule-x/current-time";
import { createEventModalPlugin } from "@schedule-x/event-modal";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import { ScheduleXCalendar, useCalendarApp } from "@schedule-x/react";
import { createScrollControllerPlugin } from "@schedule-x/scroll-controller";

import "temporal-polyfill/global";
import "@schedule-x/theme-default/dist/index.css";

import { createContext, useContext, useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  alpha,
  Box,
  Center,
  Checkbox,
  Flex,
  Group,
  isLightColor,
  lighten,
  Paper,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useLocalStorage, useResizeObserver } from "@mantine/hooks";
import {
  IconBuildingStore,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconFileText,
  IconLink,
  IconTags,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useParams } from "@tanstack/react-router";
import dayjs from "dayjs";

import {
  GetBookingsItems,
  GetCategoriesItems,
  GetDepartmentsItems,
} from "@/shared/api/generated/schemas";
import { AnchorLink } from "@/shared/components/AnchorLink";

import { bookingStatusLabels } from "../utils/status";
import classes from "./booking-calendar.module.css";
import { CalendarBookingEvent } from "./calendar-booking-event";
import { isValidCssColor } from "./is-css-color";

interface SidebarContextValue {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined,
);

function useCalendar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useCalendar must be used within CalendarProvider");
  }
  return context;
}

function CalendarProvider({ children }: { children: React.ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useLocalStorage<boolean>({
    key: "calendar:bookings-calendar:sidebar-open",
    defaultValue: true,
    getInitialValueInEffect: false,
  });

  const toggleSidebar = () => setIsSidebarOpen(isSidebarOpen ? false : true);
  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <SidebarContext.Provider
      value={{ isSidebarOpen, toggleSidebar, openSidebar, closeSidebar }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * Generates color variants from a hex color for schedule-x calendar
 * Uses Mantine's color functions for better color manipulation
 */
function generateColorVariants(hexColor: string) {
  // Main color is the original hex
  const main = hexColor;

  // Container is a lighter version using Mantine's lighten function
  // Lighten by 70% to create a subtle background color
  const container = lighten(hexColor, 0.7);

  // On container text color: dark for light backgrounds, light for dark backgrounds
  // Use isLightColor to determine appropriate contrast color
  const onContainer = isLightColor(container) ? "#000000" : "#ffffff";

  return {
    main,
    container,
    onContainer,
  };
}

type CalendarsConfig = Record<
  string,
  {
    colorName: string;
    lightColors: { main: string; container: string; onContainer: string };
    name: string;
  }
>;

/**
 * Creates calendars configuration from events based on departments
 * Uses default purplish color from Mantine when calendarColor is missing
 */
function createCalendarsConfig(
  events: CalendarEventExternal[] | undefined,
  defaultColor: string,
): CalendarsConfig | undefined {
  if (!events) return undefined;

  const calendarMap = new Map<string, { color: string; name: string }>();

  // Extract unique calendars from events using calendarId
  for (const event of events) {
    const bookingEvent = event as CalendarBookingEvent;
    if (bookingEvent.calendarId) {
      if (!calendarMap.has(bookingEvent.calendarId)) {
        // Use calendarColor if available, otherwise use default purplish color
        const color =
          bookingEvent.calendarColor &&
          isValidCssColor(bookingEvent.calendarColor)
            ? bookingEvent.calendarColor
            : defaultColor;
        calendarMap.set(bookingEvent.calendarId, {
          color,
          name: bookingEvent.departament?.name ?? "Ukjent",
        });
      }
    }
  }

  if (calendarMap.size === 0) return undefined;

  // Build calendars configuration
  // Use calendarId as the calendar key (already sanitized)
  const calendars: CalendarsConfig = {};

  for (const [calendarId, { color, name }] of calendarMap.entries()) {
    const variants = generateColorVariants(color);
    calendars[calendarId] = {
      colorName: calendarId,
      lightColors: variants,
      name,
    };
  }

  return calendars;
}
// Sanitize department name for use as calendarId to match calendar keys
const sanitizeCalendarKey = (name: string) =>
  name.toLowerCase().replace(/\s+/g, "-");

export default function BookingsCalendar({
  departments,
  bookings,
  productCategories,
}: {
  departments?: GetDepartmentsItems;
  bookings?: GetBookingsItems;
  productCategories?: GetCategoriesItems;
}) {
  const { t } = useLingui();

  const calendarsOptions = departments
    ?.map((department) => {
      return {
        id: sanitizeCalendarKey(department.name),
        name: department.name,
        color: department.calendarColor ?? "",
      };
    })
    .concat([
      {
        id: "no-departament",
        name: t`Uten avdeling`,
        color: "",
      },
    ]);

  const [selectedCalendars, setSelectedCalendars] = useState<string[]>(
    calendarsOptions?.map((calendar) => calendar.id) ?? [],
  );

  const productCategoryOptions = productCategories
    ?.map((productCategory) => {
      return {
        id: productCategory.id,
        name: productCategory.name,
      };
    })
    .concat([
      {
        id: "no-product-category",
        name: t`Uten produktkategori`,
      },
    ]);

  const [selectedProductCategoriesIds, setSelectedProductCategoriesIds] =
    useState<string[]>(
      productCategoryOptions?.map((productCategory) => productCategory.id) ??
        [],
    );

  const events = bookings?.map((booking) => {
    const isFullDay =
      dayjs(booking.departure).diff(dayjs(booking.arrival), "minutes") === 1439;

    return {
      id: booking.id,
      start: isFullDay
        ? dateToPlainDate(booking.arrival)
        : dateToZonedDateTime(booking.arrival),
      end: isFullDay
        ? dateToPlainDate(booking.departure)
        : dateToZonedDateTime(booking.departure),
      title: booking.customer.name,
      description: booking.internalInformation ?? undefined,
      expectedArrivals: `${booking.group?.guests.booked ?? 0}${booking.group?.travelLeader.booked ? `+${booking.group?.travelLeader.booked}` : ""}`,
      arrivals: `${(booking.group?.arrivals.sum ?? 0) - (booking.group?.arrivals.staff ?? 0)}${booking.group?.arrivals.staff ? `+${booking.group?.arrivals.staff}` : ""}`,
      guide: booking.guide
        ? `${booking.guide.firstName} ${booking.guide.lastName}`
        : undefined,
      departament: booking.department
        ? { id: booking.department.id, name: booking.department.name }
        : undefined,
      calendarId: booking.department?.name
        ? sanitizeCalendarKey(booking.department.name)
        : "no-departament",
      calendarColor: booking.department?.calendarColor ?? undefined,
      productCategoriesIds: (() => {
        if (booking.program && booking.program.length > 0) {
          const existingProductCategories = Array.from(
            new Set(
              booking.program
                .map((p) => p.productCategoryID)
                .filter(Boolean) as string[],
            ),
          );

          return existingProductCategories.length > 0
            ? existingProductCategories
            : ["no-product-category"];
        }

        return ["no-product-category"];
      })(),
      productCategoriesNames: Array.from(
        new Set(
          booking.program
            ?.map(
              (p) =>
                productCategories?.find((pc) => pc.id === p.productCategoryID)
                  ?.name ?? null,
            )
            .filter(Boolean) as string[],
        ),
      ),
      status: booking.status,
    };
  }) satisfies CalendarBookingEvent[] | undefined;

  const getFilteredEvents = (
    events: CalendarBookingEvent[] | undefined,
    selectedCalendars: string[],
    selectedProductCategoriesIds: string[],
  ) => {
    if (!events) return [];

    return events.filter(
      (event) =>
        selectedCalendars.includes(event.calendarId ?? "") &&
        selectedProductCategoriesIds.some((productCategoryId) =>
          event.productCategoriesIds.includes(productCategoryId),
        ),
    );
  };

  const [eventsService] = useState(() => createEventsServicePlugin());

  const theme = useMantineTheme();
  const viewMonthGrid = createViewMonthGrid();
  const viewWeek = createViewWeek();

  const [savedView, setSavedView] = useLocalStorage<string>({
    key: "calendar:bookings-calendar:view",
    defaultValue: viewWeek.name,
    getInitialValueInEffect: false,
  });

  const [savedSelectedDate, setSavedSelectedDate] = useLocalStorage<
    string | undefined
  >({
    key: "calendar:bookings-calendar:selected-date",
    defaultValue: undefined,
    getInitialValueInEffect: false,
  });

  const scrollController = createScrollControllerPlugin({
    initialScroll: "08:00",
  });

  // Use Mantine's violet color as default purplish color
  const defaultColor = theme.colors.violet[5];
  const calendarsConfig = createCalendarsConfig(events, defaultColor);

  const calendar = useCalendarApp({
    views: [
      createViewDay(),
      viewWeek,
      viewMonthGrid,
      createViewMonthAgenda(),
      createViewList(),
    ],
    plugins: [
      scrollController,
      createCurrentTimePlugin(),
      createEventModalPlugin(),
      eventsService,
    ],
    events: getFilteredEvents(
      events,
      selectedCalendars,
      selectedProductCategoriesIds,
    ), // only used for first render, later eventService needs to be used for updates
    locale: "nb-NO",
    timezone: "Europe/Oslo",
    selectedDate: savedSelectedDate
      ? dateToPlainDate(savedSelectedDate)
      : undefined,
    defaultView: savedView,
    calendars: calendarsConfig,
    callbacks: {
      beforeRender: ($app) => {
        $app.calendarState.view.subscribe((value) => {
          setSavedView(value);
        });
        $app.datePickerState.selectedDate.subscribe((value) => {
          setSavedSelectedDate(value.toString());
        });
      },
    },
  });

  useEffect(() => {
    eventsService.set(
      getFilteredEvents(
        events,
        selectedCalendars,
        selectedProductCategoriesIds,
      ),
    );
  }, [events, eventsService, selectedCalendars, selectedProductCategoriesIds]);

  return (
    <CalendarProvider>
      <BookingsCalendarContent
        calendar={calendar}
        calendarsOptions={calendarsOptions}
        productCategoryOptions={productCategoryOptions}
        selectedCalendars={selectedCalendars}
        selectedProductCategoriesIds={selectedProductCategoriesIds}
        setSelectedProductCategoriesIds={setSelectedProductCategoriesIds}
        setSelectedCalendars={setSelectedCalendars}
      />
    </CalendarProvider>
  );
}

function BookingsCalendarContent({
  calendar,
  calendarsOptions,
  productCategoryOptions,
  selectedCalendars,
  selectedProductCategoriesIds,
  setSelectedCalendars,
  setSelectedProductCategoriesIds,
}: {
  calendar: ReturnType<typeof useCalendarApp>;
  calendarsOptions: { id: string; name: string; color: string }[] | undefined;
  productCategoryOptions: { id: string; name: string }[] | undefined;
  selectedCalendars: string[];
  selectedProductCategoriesIds: string[];
  setSelectedCalendars: (calendars: string[]) => void;
  setSelectedProductCategoriesIds: (productCategoriesIds: string[]) => void;
}) {
  const { isSidebarOpen } = useCalendar();
  const [ref, rect] = useResizeObserver();

  useEffect(() => {
    console.log("rect", rect.height);
    if (ref.current && rect.height > 0) {
      (ref.current as HTMLElement).style.setProperty(
        "--calendar-height",
        `${rect.height}px`,
      );
    }
  }, [ref, rect.height]);

  return (
    <Flex
      bd="var(--sx-border)"
      style={{ borderRadius: "var(--sx-rounding-small)" }}
      flex={1}
    >
      {isSidebarOpen && (
        <Box
          p="var(--sx-spacing-padding4)"
          style={{ borderInlineEnd: "var(--sx-border)" }}
        >
          <Stack gap="xl">
            <Stack>
              <Text>
                <Trans>Avdelinger</Trans>
              </Text>
              <Stack gap="xs">
                {calendarsOptions?.map((calendarOption) => (
                  <Checkbox
                    key={calendarOption.id}
                    label={calendarOption.name}
                    color={calendarOption.color}
                    checked={selectedCalendars.includes(calendarOption.id)}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedCalendars([
                          ...selectedCalendars,
                          calendarOption.id,
                        ]);
                      } else {
                        setSelectedCalendars(
                          selectedCalendars.filter(
                            (id) => id !== calendarOption.id,
                          ),
                        );
                      }
                    }}
                  />
                ))}
              </Stack>
            </Stack>

            <Stack>
              <Text>
                <Trans>Produktkategorier</Trans>
              </Text>
              <Stack gap="xs">
                {productCategoryOptions?.map((productCategory) => (
                  <Checkbox
                    key={productCategory.id}
                    label={productCategory.name}
                    checked={selectedProductCategoriesIds.includes(
                      productCategory.id,
                    )}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedProductCategoriesIds([
                          ...selectedProductCategoriesIds,
                          productCategory.id,
                        ]);
                      } else {
                        setSelectedProductCategoriesIds(
                          selectedProductCategoriesIds.filter(
                            (id) => id !== productCategory.id,
                          ),
                        );
                      }
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}
      <Box ref={ref} flex={1} className={classes.calendar}>
        <ScheduleXCalendar
          customComponents={customComponents}
          calendarApp={calendar}
        />
      </Box>
    </Flex>
  );
}

/**
 * Gets calendar-specific CSS variable names for an event
 * Falls back to primary colors if no calendarId is set
 */
function getCalendarColorVars(calendarId: string | undefined): {
  container: string;
  onContainer: string;
  main: string;
} {
  if (!calendarId) {
    return {
      container: "var(--sx-color-primary-container)",
      onContainer: "var(--sx-color-on-primary-container)",
      main: "var(--sx-color-primary)",
    };
  }

  // Schedule-x generates CSS variables based on the calendar key
  // Format: --sx-color-{calendarKey}-container, --sx-color-{calendarKey}, etc.
  // calendarId should already be sanitized to match the calendar key
  return {
    container: `var(--sx-color-${calendarId}-container)`,
    onContainer: `var(--sx-color-on-${calendarId}-container)`,
    main: `var(--sx-color-${calendarId})`,
  };
}

// https://schedule-x.dev/docs/calendar/custom-components
const customComponents = {
  timeGridEvent: CustomTimeGridEvent,
  dateGridEvent: CustomDateGridEvent,
  monthGridEvent: CustomMonthGridEvent,
  eventModal: CustomEventModal,
  headerContentLeftPrepend: HeaderContentLeftPrepend,
};

function HeaderContentLeftPrepend() {
  const { isSidebarOpen, toggleSidebar } = useCalendar();

  return (
    <Center>
      <ActionIcon variant="light" onClick={toggleSidebar}>
        {isSidebarOpen ? <IconChevronLeft /> : <IconChevronRight />}
      </ActionIcon>
    </Center>
  );
}

function CustomEventModal({
  calendarEvent,
}: {
  calendarEvent: CalendarBookingEvent;
}) {
  const { t } = useLingui();
  const { museumId } = useParams({
    from: "/_app/(single-location)/museum/$museumId/calendar/",
  });

  const formatEventTime = (
    start: Temporal.ZonedDateTime | Temporal.PlainDate,
    end: Temporal.ZonedDateTime | Temporal.PlainDate,
  ) => {
    if (start instanceof Temporal.PlainDate) {
      return dayjs(start.toString()).format("DD.MM.YYYY");
    }
    return `${dayjs(start.toPlainDateTime().toString()).format("DD.MM.YYYY")}, ${dayjs(start.toPlainDateTime().toString()).format("HH:mm")}-${dayjs(end.toPlainDateTime().toString()).format("HH:mm")}`;
  };

  const department = calendarEvent.departament as
    | { id: string; name: string }
    | undefined;

  return (
    <Paper bg="white" withBorder p="md" shadow="xl" radius="md">
      <Stack gap="md">
        <Group gap="xs" wrap="nowrap">
          <IconCalendarEvent size={20} stroke={1.5} />
          <Text fw={600} size="lg">
            {calendarEvent.title}
          </Text>
        </Group>

        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap">
            <IconClock size={18} stroke={1.5} style={{ flexShrink: 0 }} />
            <Text size="sm">
              {formatEventTime(calendarEvent.start, calendarEvent.end)}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <IconClock size={18} stroke={1.5} style={{ flexShrink: 0 }} />
            <Text size="sm">
              {t(bookingStatusLabels[calendarEvent.status])}
            </Text>
          </Group>

          {calendarEvent.description && (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <IconFileText size={18} stroke={1.5} style={{ flexShrink: 0 }} />
              <Text size="sm">{calendarEvent.description}</Text>
            </Group>
          )}

          {calendarEvent.expectedArrivals && (
            <Group gap="xs" wrap="nowrap">
              <IconUsers size={18} stroke={1.5} style={{ flexShrink: 0 }} />
              <Text size="sm">
                {calendarEvent.expectedArrivals} forventet antall
              </Text>
            </Group>
          )}
          {calendarEvent.arrivals && (
            <Group gap="xs" wrap="nowrap">
              <IconUsers size={18} stroke={1.5} style={{ flexShrink: 0 }} />
              <Text size="sm">{calendarEvent.arrivals} ankomster</Text>
            </Group>
          )}
          {calendarEvent.guide && (
            <Group gap="xs" wrap="nowrap">
              <IconUser size={18} stroke={1.5} style={{ flexShrink: 0 }} />
              <Text size="sm">{calendarEvent.guide}</Text>
            </Group>
          )}
          {department && (
            <Group gap="xs" wrap="nowrap">
              <IconBuildingStore
                size={18}
                stroke={1.5}
                style={{ flexShrink: 0 }}
              />
              <Text size="sm">{department.name}</Text>
            </Group>
          )}

          <Group gap="xs" wrap="nowrap">
            <IconTags size={18} stroke={1.5} style={{ flexShrink: 0 }} />
            <Text size="sm">
              {calendarEvent.productCategoriesNames.length > 0
                ? calendarEvent.productCategoriesNames.join(", ")
                : t`Uten produktkategori`}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <IconLink size={18} stroke={1.5} style={{ flexShrink: 0 }} />
            <Text size="sm">
              <AnchorLink
                to={"/museum/$museumId/bookings/$bookingId"}
                params={{
                  museumId: museumId,
                  bookingId: calendarEvent.id.toString(),
                }}
              >
                <Trans>Se booking</Trans>
              </AnchorLink>
            </Text>
          </Group>
        </Stack>
      </Stack>
    </Paper>
  );
}

function CustomDateGridEvent({
  calendarEvent,
}: {
  calendarEvent: CalendarBookingEvent;
}) {
  const colors = getCalendarColorVars(calendarEvent.calendarId);

  // Determine styling based on status
  const isFullColor =
    calendarEvent.status === "arrived" ||
    calendarEvent.status === "confirmed" ||
    calendarEvent.status === "sentforinvoicing";

  const isCancelled = calendarEvent.status === "cancelled";

  const isOutline =
    calendarEvent.status === "reserved" ||
    calendarEvent.status === "request" ||
    calendarEvent.status === "confirmationsent";

  const bgColor = isFullColor
    ? colors.container
    : isOutline || isCancelled
      ? alpha("#fff", 0.8)
      : colors.container;

  const textColor = isCancelled ? "red" : colors.onContainer;

  return (
    <Flex
      pos="absolute"
      inset={0}
      bg={bgColor}
      c={textColor}
      p="var(--sx-spacing-padding1)"
      align="center"
      justify="space-between"
      gap={2}
      style={{
        borderRadius: "var(--sx-rounding-extra-small)",
        borderInlineStart: `4px solid ${colors.main}`,
        ...(isOutline || isCancelled
          ? {
              borderTop: `1px solid ${colors.main}`,
              borderRight: `1px solid ${colors.main}`,
              borderBottom: `1px solid ${colors.main}`,
            }
          : {}),
      }}
    >
      <Flex
        gap={4}
        style={{
          overflow: "hidden",
        }}
      >
        <Text
          truncate
          size="xs"
          fw={700}
          style={{ textDecoration: isCancelled ? "line-through" : "none" }}
        >
          {calendarEvent.title}
        </Text>
      </Flex>
      <Text
        size="xs"
        style={{ textDecoration: isCancelled ? "line-through" : "none" }}
      >
        {calendarEvent.expectedArrivals}
      </Text>
    </Flex>
  );
}

function CustomMonthGridEvent({
  calendarEvent,
}: {
  calendarEvent: CalendarBookingEvent;
}) {
  const colors = getCalendarColorVars(calendarEvent.calendarId);

  // Determine styling based on status
  const isFullColor =
    calendarEvent.status === "arrived" ||
    calendarEvent.status === "confirmed" ||
    calendarEvent.status === "sentforinvoicing";

  const isCancelled = calendarEvent.status === "cancelled";

  const isOutline =
    calendarEvent.status === "reserved" ||
    calendarEvent.status === "request" ||
    calendarEvent.status === "confirmationsent";

  const bgColor = isFullColor
    ? colors.container
    : isOutline || isCancelled
      ? alpha("#fff", 0.8)
      : colors.container;

  const textColor = isCancelled ? "red" : colors.onContainer;

  return (
    <Flex
      pos="absolute"
      inset={0}
      bg={bgColor}
      c={textColor}
      p="var(--sx-spacing-padding1)"
      align="center"
      justify="space-between"
      gap={2}
      style={{
        borderRadius: "var(--sx-rounding-extra-small)",
        borderInlineStart: `4px solid ${colors.main}`,
        ...(isOutline || isCancelled
          ? {
              borderTop: `1px solid ${colors.main}`,
              borderRight: `1px solid ${colors.main}`,
              borderBottom: `1px solid ${colors.main}`,
            }
          : {}),
      }}
    >
      <Flex
        gap={4}
        style={{
          overflow: "hidden",
        }}
      >
        {calendarEvent.start instanceof Temporal.ZonedDateTime && (
          <Text
            size="xs"
            fw={700}
            style={{ textDecoration: isCancelled ? "line-through" : "none" }}
          >
            {dayjs(calendarEvent.start.toPlainDateTime().toString()).format(
              "HH:mm",
            )}
          </Text>
        )}
        <Text
          truncate
          size="xs"
          fw={700}
          style={{ textDecoration: isCancelled ? "line-through" : "none" }}
        >
          {calendarEvent.title}
        </Text>
      </Flex>
      <Text
        size="xs"
        style={{ textDecoration: isCancelled ? "line-through" : "none" }}
      >
        {calendarEvent.expectedArrivals}
      </Text>
    </Flex>
  );
}

function CustomTimeGridEvent({
  calendarEvent,
}: {
  calendarEvent: CalendarBookingEvent;
}) {
  const colors = getCalendarColorVars(calendarEvent.calendarId);

  // Determine styling based on status
  const isFullColor =
    calendarEvent.status === "arrived" ||
    calendarEvent.status === "confirmed" ||
    calendarEvent.status === "sentforinvoicing";

  const isCancelled = calendarEvent.status === "cancelled";

  const isOutline =
    calendarEvent.status === "reserved" ||
    calendarEvent.status === "request" ||
    calendarEvent.status === "confirmationsent";

  const bgColor = isFullColor
    ? colors.container
    : isOutline || isCancelled
      ? alpha("#fff", 0.8)
      : colors.container;

  const textColor = isCancelled ? "red" : colors.onContainer;

  return (
    <Box
      pos="absolute"
      inset={0}
      bg={bgColor}
      c={textColor}
      p="var(--sx-spacing-padding1)"
      style={{
        borderRadius: "var(--sx-rounding-extra-small)",
        borderInlineStart: `4px solid ${colors.main}`,
        ...(isOutline || isCancelled
          ? {
              borderTop: `1px solid ${colors.main}`,
              borderRight: `1px solid ${colors.main}`,
              borderBottom: `1px solid ${colors.main}`,
            }
          : {}),
      }}
    >
      <Text
        truncate
        size="xs"
        fw={700}
        style={{ textDecoration: isCancelled ? "line-through" : "none" }}
      >
        {calendarEvent.title}
      </Text>

      <Group gap={2} wrap="nowrap">
        <IconClock size={16} stroke={1.5} style={{ flexShrink: 0 }} />
        <Text
          size="xs"
          style={{ textDecoration: isCancelled ? "line-through" : "none" }}
        >
          {dayjs(calendarEvent.start.toPlainDateTime().toString()).format(
            "HH:mm",
          )}
        </Text>
        <Text style={{ textDecoration: isCancelled ? "line-through" : "none" }}>
          -{" "}
        </Text>
        <Text
          size="xs"
          style={{ textDecoration: isCancelled ? "line-through" : "none" }}
        >
          {dayjs(calendarEvent.end.toPlainDateTime().toString()).format(
            "HH:mm",
          )}
        </Text>
      </Group>

      <Group gap={2} wrap="nowrap">
        <IconUsers size={16} stroke={1.5} style={{ flexShrink: 0 }} />
        <Text
          size="xs"
          style={{ textDecoration: isCancelled ? "line-through" : "none" }}
        >
          {calendarEvent.expectedArrivals}
        </Text>
      </Group>
    </Box>
  );
}

function dateToZonedDateTime(date: string, timeZone = "Europe/Oslo") {
  const jsDate = new Date(date);
  const instant = Temporal.Instant.fromEpochMilliseconds(jsDate.getTime());
  return instant.toZonedDateTimeISO(timeZone);
}

function dateToPlainDate(date: string) {
  return dateToZonedDateTime(date).toPlainDate();
}
